import { useEffect, useState, type FormEvent, type CSSProperties } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

/**
 * Public page behind a one-time activation link (spec 2026-09-24 increment A). Sets the password only;
 * the user then signs in normally (password + emailed code). Stores nothing: there is no session yet.
 */
const INVALID = 'This activation link is invalid or has expired. Ask your administrator for a new one.'
const label: CSSProperties = { display: 'block', fontSize: '0.875rem', color: 'var(--ra-text-secondary, #a8b2cc)', marginBottom: '0.4rem' }
const input: CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.75rem', borderRadius: 8,
  border: '1px solid var(--ra-glass-border, rgba(255,255,255,0.18))', background: 'rgba(255,255,255,0.04)', color: 'inherit', fontSize: '0.95rem' }
const note: CSSProperties = { padding: '0.65rem 0.75rem', borderRadius: 8, fontSize: '0.9rem', marginBottom: '1rem',
  border: '1px solid var(--ra-glass-border, rgba(255,255,255,0.18))' }
const button: CSSProperties = { padding: '0.65rem 1.25rem', borderRadius: 8, border: 'none', cursor: 'pointer',
  background: 'var(--ra-cyber-aqua, #22d3ee)', color: '#0a192f', fontWeight: 600, fontSize: '0.95rem' }

export default function ActivatePage({ apiBaseUrl }: { apiBaseUrl: string }) {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''
  const [pw, setPw] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(token ? null : INVALID)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  // M-9: this page's own URL carries the live `?token=`. Without a no-referrer policy, following
  // any outbound link (or a resource fetched with the browser's default Referer policy) from here
  // could leak the token via the Referer header. Set the meta tag while mounted, restoring whatever
  // was there before (there is nothing currently, but this stays correct if that ever changes).
  useEffect(() => {
    const existing = document.querySelector('meta[name="referrer"]')
    const previousContent = existing ? existing.getAttribute('content') : null
    const meta = existing ?? document.createElement('meta')
    if (!existing) {
      meta.setAttribute('name', 'referrer')
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', 'no-referrer')
    return () => {
      if (previousContent !== null) meta.setAttribute('content', previousContent)
      else meta.parentNode?.removeChild(meta)
    }
  }, [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (pw.length < 12) { setError('Password must be at least 12 characters.'); return }
    if (pw !== confirm) { setError('Passwords do not match.'); return }
    setBusy(true); setError(null)
    try {
      const r = await fetch(`${apiBaseUrl}/api/auth/activate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, password: pw }) })
      const body = await r.json().catch(() => ({}))
      if (r.ok && body.success) setDone(true); else setError(body.error ?? INVALID)
    } catch { setError('Could not reach Foundations. Check your connection and try again.') }
    finally { setBusy(false) }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', background: 'var(--ra-navy, #0a192f)' }}>
      <main style={{ maxWidth: 480, width: '100%', padding: '2rem', borderRadius: 12, border: '1px solid var(--ra-glass-border, rgba(255,255,255,0.18))', background: 'rgba(255,255,255,0.03)' }}>
        <h1 style={{ fontSize: '1.4rem', marginTop: 0 }}>Set your Foundations password</h1>
        {done ? (
          <>
            <div role="status" style={note}>✓ Your password is set. Sign in with your email and new password; we will email you a sign-in code.</div>
            <button type="button" style={button} onClick={() => navigate('/login', { replace: true })}>Go to sign in</button>
          </>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            {error && <div role="alert" style={note}>⚠ {error}</div>}
            {token && (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="act-new" style={label}>New password</label>
                  <input id="act-new" type="password" autoComplete="new-password" style={input} value={pw} onChange={(e) => setPw(e.target.value)} />
                </div>
                <div style={{ marginBottom: '1.25rem' }}>
                  <label htmlFor="act-confirm" style={label}>Confirm password</label>
                  <input id="act-confirm" type="password" autoComplete="new-password" style={input} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                </div>
                <button type="submit" disabled={busy} style={button}>{busy ? 'Setting…' : 'Set password'}</button>
              </>
            )}
          </form>
        )}
      </main>
    </div>
  )
}
